import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../api/api';
import { DataForm } from '../components/forms';
import { DataTable } from '../components/tables';
import './WellCasingCementing.css';

const WellCasingCementing = () => {
    // Core state
    const [wells, setWells] = useState([]);
    const [selectedWellId, setSelectedWellId] = useState('');
    const [reports, setReports] = useState([]);
    const [selectedReportId, setSelectedReportId] = useState('');
    const [currentReport, setCurrentReport] = useState(null);
    const [selectedWell, setSelectedWell] = useState(null);
    
    // Data state
    const [dailyCasingData, setDailyCasingData] = useState([]);
    const [casingRunData, setCasingRunData] = useState([]);
    const [casingTallyData, setCasingTallyData] = useState([]);
    const [cementJobData, setCementJobData] = useState([]);
    const [casingData, setCasingData] = useState([]);
    
    // Column configurations
    const [dailyCasingColumns, setDailyCasingColumns] = useState([]);
    const [casingRunColumns, setCasingRunColumns] = useState([]);
    const [casingTallyColumns, setCasingTallyColumns] = useState([]);
    const [cementJobColumns, setCementJobColumns] = useState([]);
    const [casingColumns, setCasingColumns] = useState([]);
    
    // UI state
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState('');
    const [activeTab, setActiveTab] = useState('overview');
    const [showDataForm, setShowDataForm] = useState(false);
    const [dataFormConfig, setDataFormConfig] = useState({});
    const [editingRecord, setEditingRecord] = useState(null);
    
    // Selected casing run for tally and cement job context
    const [selectedCasingRun, setSelectedCasingRun] = useState(null);
    
    // Tally modal state
    const [showTallyModal, setShowTallyModal] = useState(false);
    const [tallyForRun, setTallyForRun] = useState([]);
    
    // Collapsible sections state
    const [collapsedSections, setCollapsedSections] = useState({
        overview: false,
        activities: false,
        runs: false,
        tally: false,
        cement: false,
        summary: false
    });

    // Statistics state
    const [statistics, setStatistics] = useState({
        totalActivities: 0,
        totalRuns: 0,
        totalCementJobs: 0,
        totalCasingLength: 0,
        averageCementVolume: 0
    });

    // Quick actions state
    const [quickActions, setQuickActions] = useState({
        showQuickAdd: false,
        selectedQuickAction: null
    });

    // Export state
    const [exporting, setExporting] = useState(false);

    const role = localStorage.getItem('role') || 'admin';

    // Load initial data
    useEffect(() => {
        loadWells();
        loadColumnConfigurations();
    }, []);

    // Load reports when well is selected
    useEffect(() => {
        if (selectedWellId) {
            loadReports();
            loadWellDetails();
        } else {
            setReports([]);
            setSelectedReportId('');
            setCurrentReport(null);
            setSelectedWell(null);
        }
    }, [selectedWellId]);

    // Load data when report is selected
    useEffect(() => {
        if (selectedReportId && selectedWellId) {
            loadAllData();
        } else {
            clearAllData();
        }
    }, [selectedReportId, selectedWellId]);

    // Load tally when casing run is selected
    useEffect(() => {
        if (selectedCasingRun) {
            loadCasingTally(selectedCasingRun.CasingRunID);
        }
    }, [selectedCasingRun]);

    // Calculate statistics when data changes
    useEffect(() => {
        calculateStatistics();
    }, [dailyCasingData, casingRunData, cementJobData, casingData]);

    const loadWells = async () => {
        try {
            const response = await apiFetch('/api/wells');
            setWells(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Failed to load wells:', error);
            showToast('❌ Failed to load wells');
        }
    };

    const loadReports = async () => {
        try {
            const response = await apiFetch(`/api/ddr/reports?wellId=${selectedWellId}`);
            setReports(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Failed to load reports:', error);
            showToast('❌ Failed to load reports');
        }
    };

    const loadWellDetails = async () => {
        try {
            const response = await apiFetch(`/api/wells/${selectedWellId}`);
            setSelectedWell(response);
        } catch (error) {
            console.error('Failed to load well details:', error);
        }
    };

    const loadColumnConfigurations = async () => {
        try {
            const response = await apiFetch('/api/config/columns');
            const allConfigs = Array.isArray(response) ? response : [];
            
            const dailyCasingConfig = allConfigs.find(config => config.tableName === 'DailyCasing');
            const casingRunConfig = allConfigs.find(config => config.tableName === 'CasingRun');
            const casingTallyConfig = allConfigs.find(config => config.tableName === 'CasingTally');
            const cementJobConfig = allConfigs.find(config => config.tableName === 'CementJob');
            const casingConfig = allConfigs.find(config => config.tableName === 'Casing');
            
            if (dailyCasingConfig) setDailyCasingColumns(dailyCasingConfig.columns.sort((a, b) => a.order - b.order));
            if (casingRunConfig) setCasingRunColumns(casingRunConfig.columns.sort((a, b) => a.order - b.order));
            if (casingTallyConfig) setCasingTallyColumns(casingTallyConfig.columns.sort((a, b) => a.order - b.order));
            if (cementJobConfig) setCementJobColumns(cementJobConfig.columns.sort((a, b) => a.order - b.order));
            if (casingConfig) setCasingColumns(casingConfig.columns.sort((a, b) => a.order - b.order));
        } catch (error) {
            console.error('Failed to load column configurations:', error);
            showToast('❌ Failed to load configurations');
        }
    };

    const loadAllData = async () => {
        setIsLoading(true);
        try {
            // Load current report details
            const reportResponse = await apiFetch(`/api/ddr/reports/${selectedReportId}`);
            setCurrentReport(reportResponse);

            // Load all casing-related data
            await Promise.all([
                loadDailyCasingData(),
                loadCasingRunData(),
                loadCementJobData(),
                loadCasingData()
            ]);
        } catch (error) {
            console.error('Failed to load data:', error);
            showToast('❌ Failed to load data');
        }
        setIsLoading(false);
    };

    const loadDailyCasingData = async () => {
        try {
            const response = await apiFetch(`/api/db/DailyCasing?ReportID=${selectedReportId}`);
            setDailyCasingData(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Failed to load daily casing data:', error);
        }
    };

    const loadCasingRunData = async () => {
        try {
            const response = await apiFetch(`/api/db/CasingRun?WellID=${selectedWellId}`);
            setCasingRunData(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Failed to load casing run data:', error);
        }
    };

    const loadCementJobData = async () => {
        try {
            // Load cement jobs for all casing runs of this well
            const response = await apiFetch(`/api/db/CementJob`);
            const allJobs = Array.isArray(response) ? response : [];
            
            // Filter cement jobs that belong to casing runs of this well
            const casingRuns = await apiFetch(`/api/db/CasingRun?WellID=${selectedWellId}`);
            const runIds = casingRuns.map(run => run.CasingRunID);
            const filteredJobs = allJobs.filter(job => runIds.includes(job.CasingRunID));
            
            setCementJobData(filteredJobs);
        } catch (error) {
            console.error('Failed to load cement job data:', error);
        }
    };

    const loadCasingData = async () => {
        try {
            const response = await apiFetch(`/api/db/Casing?WellID=${selectedWellId}`);
            setCasingData(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Failed to load casing data:', error);
        }
    };

    const loadCasingTally = async (casingRunId) => {
        try {
            const response = await apiFetch(`/api/db/CasingTally?CasingRunID=${casingRunId}`);
            setTallyForRun(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Failed to load casing tally:', error);
            setTallyForRun([]);
        }
    };

    const clearAllData = () => {
        setDailyCasingData([]);
        setCasingRunData([]);
        setCasingTallyData([]);
        setCementJobData([]);
        setCasingData([]);
        setSelectedCasingRun(null);
        setTallyForRun([]);
    };

    const calculateStatistics = () => {
        const stats = {
            totalActivities: dailyCasingData.length,
            totalRuns: casingRunData.length,
            totalCementJobs: cementJobData.length,
            totalCasingLength: casingData.reduce((sum, casing) => sum + (parseFloat(casing.Length) || 0), 0),
            averageCementVolume: cementJobData.length > 0 
                ? cementJobData.reduce((sum, job) => sum + (parseFloat(job.CementVolume) || 0), 0) / cementJobData.length 
                : 0
        };
        setStatistics(stats);
    };

    const handleWellChange = (e) => {
        setSelectedWellId(e.target.value);
        setSelectedReportId('');
        clearAllData();
    };

    const handleReportChange = (e) => {
        setSelectedReportId(e.target.value);
    };

    const toggleSection = (sectionId) => {
        setCollapsedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const showToast = (message) => {
        setToast(message);
        setTimeout(() => setToast(''), 3000);
    };

    const handleDataChange = async () => {
        // Refresh data after any changes
        if (selectedReportId && selectedWellId) {
            await loadAllData();
        }
    };



    const renderWellReportSelector = () => (
        <div className="well-report-selector">
            <div className="selector-row">
                <div className="selector-group">
                    <label htmlFor="well-select">Well:</label>
                    <select
                        id="well-select"
                        value={selectedWellId}
                        onChange={handleWellChange}
                        className="form-select"
                    >
                        <option value="">Select a well...</option>
                        {wells.map(well => (
                            <option key={well.WellID} value={well.WellID}>
                                {well.WellName} {well.Field ? `- ${well.Field}` : ''}
                            </option>
                        ))}
                    </select>
                </div>
                
                <div className="selector-group">
                    <label htmlFor="report-select">Report:</label>
                    <select
                        id="report-select"
                        value={selectedReportId}
                        onChange={handleReportChange}
                        className="form-select"
                        disabled={!selectedWellId}
                    >
                        <option value="">Select a report...</option>
                        {reports.map(report => (
                            <option key={report.ReportID} value={report.ReportID}>
                                {new Date(report.ReportDate).toLocaleDateString()} 
                                {report.Status ? ` - ${report.Status}` : ''}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            
            {selectedWell && currentReport && (
                <div className="context-info">
                    <div className="well-info">
                        <strong>{selectedWell.WellName}</strong>
                        {selectedWell.Field && <span> • Field: {selectedWell.Field}</span>}
                        {selectedWell.TotalDepth && <span> • TD: {selectedWell.TotalDepth}m</span>}
                        {selectedWell.Operator && <span> • Operator: {selectedWell.Operator}</span>}
                    </div>
                    <div className="report-info">
                        Report Date: <strong>{new Date(currentReport.ReportDate).toLocaleDateString()}</strong>
                        {currentReport.Status && <span> • Status: {currentReport.Status}</span>}
                    </div>
                </div>
            )}
        </div>
    );



    const renderQuickActions = () => (
        <div className="quick-actions">
            <button
                className="quick-action-btn small"
                onClick={() => handleQuickAction('addActivity')}
                title="Add Daily Casing Activity"
            >
                📋 Add Activity
            </button>
            <button
                className="quick-action-btn small"
                onClick={() => handleQuickAction('addCasingRun')}
                title="Add Casing Run & Tally"
            >
                🔧 Add Casing Run
            </button>
            <button
                className="quick-action-btn small"
                onClick={() => handleQuickAction('addCementJob')}
                title="Add Cement Job"
            >
                🧱 Add Cement Job
            </button>
            <button
                className="quick-action-btn small"
                onClick={() => handleQuickAction('exportData')}
                disabled={exporting}
                title="Export Data"
            >
                {exporting ? '⏳ Exporting...' : '📤 Export'}
            </button>
        </div>
    );

    const renderTabNavigation = () => (
        <div className="tab-navigation">
            <button
                className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
            >
                📊 Overview
            </button>
            <button
                className={`tab ${activeTab === 'activities' ? 'active' : ''}`}
                onClick={() => setActiveTab('activities')}
            >
                📋 Daily Activities
            </button>
            <button
                className={`tab ${activeTab === 'runs' ? 'active' : ''}`}
                onClick={() => setActiveTab('runs')}
            >
                🔧 Casing Runs
            </button>
            <button
                className={`tab ${activeTab === 'cement' ? 'active' : ''}`}
                onClick={() => setActiveTab('cement')}
            >
                🧱 Cement Jobs
            </button>
            <button
                className={`tab ${activeTab === 'summary' ? 'active' : ''}`}
                onClick={() => setActiveTab('summary')}
            >
                📈 Summary
            </button>
        </div>
    );

    const renderDataSummary = () => (
        <div className="data-summary">
            <div className="summary-section">
                <h4>📊 Data Summary</h4>
                <div className="summary-grid">
                    <div className="summary-item">
                        <span className="summary-label">Total Activities:</span>
                        <span className="summary-value">{statistics.totalActivities}</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Casing Runs:</span>
                        <span className="summary-value">{statistics.totalRuns}</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Cement Jobs:</span>
                        <span className="summary-value">{statistics.totalCementJobs}</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Total Casing Length:</span>
                        <span className="summary-value">{statistics.totalCasingLength.toFixed(1)}m</span>
                    </div>
                </div>
            </div>
        </div>
    );

    const CollapsibleSection = ({ title, children, sectionId, isEmpty = false, onAdd = null }) => {
        const isCollapsed = collapsedSections[sectionId];
        
        return (
            <div className="collapsible-section">
                <div className="section-header" onClick={() => toggleSection(sectionId)}>
                    <div className="section-title">
                        <span className={`collapse-icon ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                            {isCollapsed ? '▶' : '▼'}
                        </span>
                        {title}
                    </div>
                    {onAdd && (role === 'admin' || role === 'read-write') && (
                        <button
                            className="add-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAdd();
                            }}
                        >
                            ➕ Add
                        </button>
                    )}
                </div>
                {!isCollapsed && (
                    <div className="section-content">
                        {isEmpty && (role === 'admin' || role === 'read-write') ? (
                            <div className="empty-section">
                                <p>No data available</p>
                                {onAdd && (
                                    <button className="add-data-btn" onClick={onAdd}>
                                        ➕ Add Data
                                    </button>
                                )}
                            </div>
                        ) : (
                            children
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderTimelineView = () => {
        const timelineData = dailyCasingData
            .sort((a, b) => new Date(a.StartTime) - new Date(b.StartTime))
            .map(activity => ({
                ...activity,
                duration: activity.StartTime && activity.EndTime 
                    ? (new Date(activity.EndTime) - new Date(activity.StartTime)) / (1000 * 60 * 60) 
                    : 0
            }));

        return (
            <div className="timeline-container">
                {timelineData.map((activity, index) => (
                    <div key={activity.CasingActivityID || index} className="timeline-item">
                        <div className="timeline-marker"></div>
                        <div className="timeline-content">
                            <div className="timeline-header">
                                <div className="timeline-time">
                                    {activity.StartTime && new Date(activity.StartTime).toLocaleTimeString()} 
                                    {activity.EndTime && ` - ${new Date(activity.EndTime).toLocaleTimeString()}`}
                                    {activity.duration > 0 && (
                                        <span className="duration">({activity.duration.toFixed(1)}h)</span>
                                    )}
                                </div>
                                <div className="timeline-actions">
                                    {(role === 'admin' || role === 'read-write') && (
                                        <>
                                            <button
                                                className="edit-btn-small"
                                                onClick={() => handleEditRecord('DailyCasing', activity)}
                                                title="Edit activity"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                className="delete-btn-small"
                                                onClick={() => handleDeleteRecord('DailyCasing', activity)}
                                                title="Delete activity"
                                            >
                                                🗑️
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="timeline-description">
                                <strong>{activity.ActivityDescription}</strong>
                            </div>
                            <div className="timeline-details">
                                {activity.DepthIn && <span>Depth In: {activity.DepthIn}m</span>}
                                {activity.DepthOut && <span>Depth Out: {activity.DepthOut}m</span>}
                                {activity.Personnel && <span>Personnel: {activity.Personnel}</span>}
                            </div>
                            {activity.Remarks && (
                                <div className="timeline-remarks">
                                    <em>{activity.Remarks}</em>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {timelineData.length === 0 && (
                    <div className="empty-timeline">
                        <p>No casing activities recorded for this report</p>
                    </div>
                )}
            </div>
        );
    };

    const renderCasingRunsTable = () => {
        const runsWithActions = casingRunData.map(run => ({
            ...run,
            actions: (
                <div className="action-buttons">
                    <button
                        className="action-btn view-btn"
                        onClick={() => handleViewTally(run)}
                        title="View Tally"
                    >
                        📋 Tally
                    </button>
                    {(role === 'admin' || role === 'read-write') && (
                        <>
                            <button
                                className="action-btn edit-btn"
                                onClick={() => handleEditRecord('CasingRun', run)}
                                title="Edit Run"
                            >
                                ✏️
                            </button>
                            <button
                                className="action-btn delete-btn"
                                onClick={() => handleDeleteRecord('CasingRun', run)}
                                title="Delete Run"
                            >
                                🗑️
                            </button>
                        </>
                    )}
                </div>
            )
        }));

        return (
            <DataTable
                data={runsWithActions}
                columns={[
                    ...casingRunColumns,
                    {
                        name: 'actions',
                        label: 'Actions',
                        type: 'custom',
                        order: 999
                    }
                ]}
                role={role}
                tableName="CasingRun"
                title="Casing Runs"
                onDataChange={handleDataChange}
                itemsPerPage={10}
            />
        );
    };

    const renderCementJobsTable = () => {
        // Join cement jobs with casing run information for better display
        const jobsWithRunInfo = cementJobData.map(job => {
            const associatedRun = casingRunData.find(run => run.CasingRunID === job.CasingRunID);
            return {
                ...job,
                CasingSize: associatedRun?.CasingSize || 'N/A',
                Grade: associatedRun?.Grade || 'N/A',
                actions: (
                    <div className="action-buttons">
                        {(role === 'admin' || role === 'read-write') && (
                            <>
                                <button
                                    className="action-btn edit-btn"
                                    onClick={() => handleEditRecord('CementJob', job)}
                                    title="Edit Cement Job"
                                >
                                    ✏️
                                </button>
                                <button
                                    className="action-btn delete-btn"
                                    onClick={() => handleDeleteRecord('CementJob', job)}
                                    title="Delete Cement Job"
                                >
                                    🗑️
                                </button>
                            </>
                        )}
                    </div>
                )
            };
        });

        return (
            <DataTable
                data={jobsWithRunInfo}
                columns={[
                    {
                        name: 'CasingSize',
                        label: 'Casing Size',
                        type: 'string',
                        order: 0
                    },
                    {
                        name: 'Grade',
                        label: 'Grade',
                        type: 'string',
                        order: 1
                    },
                    ...cementJobColumns.filter(col => col.name !== 'CasingRunID'),
                    {
                        name: 'actions',
                        label: 'Actions',
                        type: 'custom',
                        order: 999
                    }
                ]}
                role={role}
                tableName="CementJob"
                title="Cement Jobs"
                onDataChange={handleDataChange}
                itemsPerPage={10}
            />
        );
    };

    const renderCasingSummaryTable = () => {
        return (
            <DataTable
                data={casingData.map(casing => ({
                    ...casing,
                    actions: (
                        <div className="action-buttons">
                            {(role === 'admin' || role === 'read-write') && (
                                <>
                                    <button
                                        className="action-btn edit-btn"
                                        onClick={() => handleEditRecord('Casing', casing)}
                                        title="Edit Casing"
                                    >
                                        ✏️
                                    </button>
                                    <button
                                        className="action-btn delete-btn"
                                        onClick={() => handleDeleteRecord('Casing', casing)}
                                        title="Delete Casing"
                                    >
                                        🗑️
                                    </button>
                                </>
                            )}
                        </div>
                    )
                }))}
                columns={[
                    ...casingColumns,
                    {
                        name: 'actions',
                        label: 'Actions',
                        type: 'custom',
                        order: 999
                    }
                ]}
                role={role}
                tableName="Casing"
                title="Casing Summary"
                onDataChange={handleDataChange}
                itemsPerPage={10}
            />
        );
    };

    const renderTallyModal = () => {
        if (!showTallyModal || !selectedCasingRun) return null;

        return (
            <div className="modal-overlay">
                <div className="modal-content tally-modal">
                    <div className="modal-header">
                        <h3>
                            Casing Tally - {selectedCasingRun.CasingSize} 
                            {selectedCasingRun.Grade && ` ${selectedCasingRun.Grade}`}
                        </h3>
                        <button
                            className="close-btn"
                            onClick={() => {
                                setShowTallyModal(false);
                                setSelectedCasingRun(null);
                                setTallyForRun([]);
                            }}
                        >
                            ✕
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="tally-info">
                            <div className="run-summary">
                                <div className="run-details">
                                    <span><strong>Size:</strong> {selectedCasingRun.CasingSize}</span>
                                    <span><strong>Weight:</strong> {selectedCasingRun.CasingWeight}</span>
                                    <span><strong>Grade:</strong> {selectedCasingRun.Grade}</span>
                                    <span><strong>Connection:</strong> {selectedCasingRun.Connection}</span>
                                </div>
                                {(role === 'admin' || role === 'read-write') && (
                                    <button
                                        className="add-btn"
                                        onClick={() => handleAddRecord('CasingTally', {
                                            CasingRunID: selectedCasingRun.CasingRunID
                                        })}
                                    >
                                        ➕ Add Joint
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="tally-table">
                            <DataTable
                                data={tallyForRun.map(joint => ({
                                    ...joint,
                                    actions: (
                                        <div className="action-buttons">
                                            {(role === 'admin' || role === 'read-write') && (
                                                <>
                                                    <button
                                                        className="action-btn edit-btn"
                                                        onClick={() => handleEditRecord('CasingTally', joint)}
                                                        title="Edit Joint"
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        className="action-btn delete-btn"
                                                        onClick={() => handleDeleteRecord('CasingTally', joint)}
                                                        title="Delete Joint"
                                                    >
                                                        🗑️
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )
                                }))}
                                columns={[
                                    ...casingTallyColumns.filter(col => col.name !== 'CasingRunID'),
                                    {
                                        name: 'actions',
                                        label: 'Actions',
                                        type: 'custom',
                                        order: 999
                                    }
                                ]}
                                role={role}
                                tableName="CasingTally"
                                title=""
                                onDataChange={() => {
                                    loadCasingTally(selectedCasingRun.CasingRunID);
                                    handleDataChange();
                                }}
                                itemsPerPage={20}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // --- Layout Adaptation ---
    // Remove modal for DataForm, use right panel instead
    // Add selectedSection and selectedRecord for context
    const [selectedSection, setSelectedSection] = useState(null); // 'DailyCasing', 'CasingRun', etc.
    const [selectedRecord, setSelectedRecord] = useState(null);

    // --- Handlers ---
    // Adapt Add/Edit handlers to set section/record and show form in right panel
    const handleAddRecord = (tableName, defaultData = {}, formType = 'single') => {
        let config = {};
        let data = { ...defaultData };
        
        // Set default foreign keys based on table
        if (tableName === 'DailyCasing') {
            data.ReportID = selectedReportId;
            config = {
                formType: 'single',
                tableName: 'DailyCasing',
                data,
                title: 'Add New Daily Casing Activity'
            };
        } else if (tableName === 'CasingRun') {
            data.WellID = selectedWellId;
            // Dual table form for CasingRun + CasingTally
            config = {
                formType: 'dual',
                tables: { parent: 'CasingRun', child: 'CasingTally' },
                identityColumns: { parent: 'CasingRunID', child: 'TallyID' },
                matchKey: { parentKey: 'CasingRunID', childForeignKey: 'CasingRunID' },
                defaultParentData: data,
                hiddenParentFields: ['WellID'],
                title: 'Add New Casing Run & Tally'
            };
        } else if (tableName === 'CementJob') {
            if (selectedCasingRun) {
                data.CasingRunID = selectedCasingRun.CasingRunID;
            }
            config = {
                formType: 'single',
                tableName: 'CementJob',
                data,
                title: 'Add New Cement Job'
            };
        } else if (tableName === 'Casing') {
            data.WellID = selectedWellId;
            if (currentReport) {
                data.ReportDate = currentReport.ReportDate;
            }
            config = {
                formType: 'single',
                tableName: 'Casing',
                data,
                title: 'Add New Casing Record'
            };
        }
        
        setDataFormConfig(config);
        setEditingRecord(null);
        setShowDataForm(true);
        setSelectedSection(tableName);
        setSelectedRecord(null);
    };

    // Enhanced Edit Record Handler
    const handleEditRecord = (tableName, record, formType = 'single') => {
        let config = {};
        
        if (tableName === 'DailyCasing') {
            config = {
                formType: 'single',
                tableName: 'DailyCasing',
                data: { ...record },
                title: 'Edit Daily Casing Activity'
            };
        } else if (tableName === 'CasingRun') {
            // Dual table form for CasingRun + CasingTally
            config = {
                formType: 'dual',
                tables: { parent: 'CasingRun', child: 'CasingTally' },
                identityColumns: { parent: 'CasingRunID', child: 'TallyID' },
                matchKey: { parentKey: 'CasingRunID', childForeignKey: 'CasingRunID' },
                recordId: record.CasingRunID,
                hiddenParentFields: ['WellID'],
                title: 'Edit Casing Run & Tally'
            };
        } else if (tableName === 'CementJob') {
            config = {
                formType: 'single',
                tableName: 'CementJob',
                data: { ...record },
                title: 'Edit Cement Job'
            };
        } else if (tableName === 'Casing') {
            config = {
                formType: 'single',
                tableName: 'Casing', 
                data: { ...record },
                title: 'Edit Casing Record'
            };
        }
        
        setDataFormConfig(config);
        setEditingRecord(record);
        setShowDataForm(true);
        setSelectedSection(tableName);
        setSelectedRecord(record);
    };

    // Cancel handler for DataForm
    const handleCancelForm = () => {
        setShowDataForm(false);
        setEditingRecord(null);
        setDataFormConfig({});
        setSelectedSection(null);
        setSelectedRecord(null);
    };

    // DataForm Save Handler
    const handleDataFormSave = useCallback(async (result) => {
        try {
            if (dataFormConfig.formType === 'dual') {
                console.log('✅ Dual table save completed:', result);
                showToast('✅ Casing run and tally saved successfully');
            } else {
                console.log('✅ Single table save completed:', result);
                showToast('✅ Data saved successfully');
            }
            
            setShowDataForm(false);
            setEditingRecord(null);
            setDataFormConfig({});
            setSelectedSection(null);
            setSelectedRecord(null);
            
            // Refresh data
            await handleDataChange();
        } catch (error) {
            console.error('❌ Failed to save data:', error);
            showToast('❌ Failed to save data: ' + error.message);
        }
    }, [dataFormConfig]);

    const handleViewTally = (casingRun) => {
        setSelectedCasingRun(casingRun);
        setShowTallyModal(true);
    };

    const handleDeleteRecord = async (tableName, record) => {
        if (!window.confirm('Are you sure you want to delete this record?')) {
            return;
        }

        try {
            // Determine the primary key field name
            let primaryKey = 'ID';
            if (tableName === 'DailyCasing') primaryKey = 'CasingActivityID';
            else if (tableName === 'CasingRun') primaryKey = 'CasingRunID';
            else if (tableName === 'CasingTally') primaryKey = 'TallyID';
            else if (tableName === 'CementJob') primaryKey = 'CementJobID';
            else if (tableName === 'Casing') primaryKey = 'CasingID';

            await apiFetch(`/api/db/${tableName}/${record[primaryKey]}`, {
                method: 'DELETE'
            });

            showToast('✅ Record deleted successfully');
            await handleDataChange();
        } catch (error) {
            console.error('Failed to delete record:', error);
            showToast('❌ Failed to delete record');
        }
    };

    // Quick Actions Handler
    const handleQuickAction = (action) => {
        switch (action) {
            case 'addActivity':
                handleAddRecord('DailyCasing');
                break;
            case 'addCasingRun':
                handleAddRecord('CasingRun');
                break;
            case 'addCementJob':
                handleAddRecord('CementJob');
                break;
            case 'exportData':
                handleExportData();
                break;
            default:
                break;
        }
        setQuickActions({ showQuickAdd: false, selectedQuickAction: null });
    };

    // Export Data Handler
    const handleExportData = async () => {
        if (!selectedWellId || !selectedReportId) {
            showToast('❌ Please select a well and report first');
            return;
        }

        setExporting(true);
        try {
            const exportData = {
                well: selectedWell,
                report: currentReport,
                dailyCasing: dailyCasingData,
                casingRuns: casingRunData,
                cementJobs: cementJobData,
                casing: casingData,
                statistics: statistics,
                exportDate: new Date().toISOString()
            };

            const dataStr = JSON.stringify(exportData, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `casing_cementing_${selectedWell?.WellName}_${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            showToast('✅ Data exported successfully');
        } catch (error) {
            console.error('Export failed:', error);
            showToast('❌ Export failed');
        }
        setExporting(false);
    };

    const renderLeftPanel = () => (
        <div className="bha-list-panel">
            {/* Timeline/Activities */}
            <div className="section-header clickable" onClick={() => setSelectedSection('DailyCasing')}>
                <h3>Daily Casing Activities</h3>
                <button className="add-btn" onClick={e => { e.stopPropagation(); handleAddRecord('DailyCasing'); }}>➕ Add</button>
            </div>
            <div>
                {/* Timeline view, each item clickable for edit */}
                {dailyCasingData.map(activity => (
                    <div key={activity.CasingActivityID} className="bha-row" onClick={() => handleEditRecord('DailyCasing', activity)}>
                        <strong>{activity.ActivityDescription}</strong> ({activity.StartTime && new Date(activity.StartTime).toLocaleTimeString()})
                    </div>
                ))}
            </div>
            {/* Casing Runs */}
            <div className="section-header clickable" onClick={() => setSelectedSection('CasingRun')}>
                <h3>Casing Runs</h3>
                <button className="add-btn" onClick={e => { e.stopPropagation(); handleAddRecord('CasingRun'); }}>➕ Add</button>
            </div>
            <div>
                {casingRunData.map(run => (
                    <div key={run.CasingRunID} className="bha-row" onClick={() => handleEditRecord('CasingRun', run)}>
                        <strong>{run.CasingSize}</strong> {run.Grade}
                    </div>
                ))}
            </div>
            {/* Cement Jobs */}
            <div className="section-header clickable" onClick={() => setSelectedSection('CementJob')}>
                <h3>Cement Jobs</h3>
                <button className="add-btn" onClick={e => { e.stopPropagation(); handleAddRecord('CementJob'); }}>➕ Add</button>
            </div>
            <div>
                {cementJobData.map(job => (
                    <div key={job.CementJobID} className="bha-row" onClick={() => handleEditRecord('CementJob', job)}>
                        Cement Job #{job.CementJobID}
                    </div>
                ))}
            </div>
            {/* Casing Summary */}
            <div className="section-header clickable" onClick={() => setSelectedSection('Casing')}>
                <h3>Casing Summary</h3>
                <button className="add-btn" onClick={e => { e.stopPropagation(); handleAddRecord('Casing'); }}>➕ Add</button>
            </div>
            <div>
                {casingData.map(casing => (
                    <div key={casing.CasingID} className="bha-row" onClick={() => handleEditRecord('Casing', casing)}>
                        <strong>{casing.CasingSize}</strong> {casing.Grade}
                    </div>
                ))}
            </div>
        </div>
    );

    const renderRightPanel = () => (
        <div className="bha-details-panel">
            {showDataForm ? (
                dataFormConfig.formType === 'dual' ? (
                    <DataForm
                        tables={dataFormConfig.tables}
                        identityColumns={dataFormConfig.identityColumns}
                        matchKey={dataFormConfig.matchKey}
                        recordId={dataFormConfig.recordId}
                        defaultParentData={dataFormConfig.defaultParentData}
                        hiddenParentFields={dataFormConfig.hiddenParentFields}
                        columnsPerRow={3}
                        onSave={handleDataFormSave}
                        onCancel={handleCancelForm}
                        title={dataFormConfig.title}
                    />
                ) : (
                    <DataForm
                        data={dataFormConfig.data}
                        tableName={dataFormConfig.tableName}
                        columnsPerRow={2}
                        onSave={handleDataFormSave}
                        onCancel={handleCancelForm}
                        title={dataFormConfig.title}
                    />
                )
            ) : (
                <div className="placeholder-container">
                    <div className="placeholder-content">
                        <div className="placeholder-icon">🛠️</div>
                        <p>Select or add a record to begin editing Casing/Cementing data.</p>
                    </div>
                </div>
            )}
        </div>
    );

    if (isLoading) {
        return (
            <div className="casing-cementing-container loading">
                <div className="loading-spinner">Loading...</div>
            </div>
        );
    }

    return (
        <div className="wellbha-container fullscreen">
            <div className="wellbha-header">
                <div className="header-main">
                    <h1>Casing & Cementing Operations</h1>
                    {renderWellReportSelector()}
                </div>
                {renderQuickActions()}
            </div>
            <div className="wellbha-content-grid">
                {renderLeftPanel()}
                {renderRightPanel()}
            </div>
            {renderTabNavigation()}
            {activeTab === 'overview' && (
                <div className="overview-content">
                    {renderDataSummary()}
                    
                    <CollapsibleSection
                        title="Daily Casing Activities Timeline"
                        sectionId="activities"
                        isEmpty={dailyCasingData.length === 0}
                        onAdd={() => handleAddRecord('DailyCasing')}
                    >
                        {renderTimelineView()}
                    </CollapsibleSection>

                    <CollapsibleSection
                        title="Casing Runs Summary"
                        sectionId="runs"
                        isEmpty={casingRunData.length === 0}
                        onAdd={() => handleAddRecord('CasingRun')}
                    >
                        <div className="runs-summary">
                            {casingRunData.map(run => (
                                <div key={run.CasingRunID} className="run-card">
                                    <div className="run-header">
                                        <h4>{run.CasingSize} {run.Grade}</h4>
                                        <div className="run-actions">
                                            <button
                                                className="view-btn"
                                                onClick={() => handleViewTally(run)}
                                            >
                                                📋 Tally
                                            </button>
                                            {(role === 'admin' || role === 'read-write') && (
                                                <>
                                                    <button
                                                        className="edit-btn"
                                                        onClick={() => handleEditRecord('CasingRun', run)}
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        className="delete-btn"
                                                        onClick={() => handleDeleteRecord('CasingRun', run)}
                                                    >
                                                        🗑️
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="run-details">
                                        <span><strong>Weight:</strong> {run.CasingWeight}</span>
                                        <span><strong>Connection:</strong> {run.Connection}</span>
                                        <span><strong>Run Date:</strong> {run.RunDate && new Date(run.RunDate).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CollapsibleSection>

                    <CollapsibleSection
                        title="Cement Jobs Summary"
                        sectionId="cement"
                        isEmpty={cementJobData.length === 0}
                        onAdd={() => handleAddRecord('CementJob')}
                    >
                        <div className="cement-summary">
                            {cementJobData.map(job => {
                                const associatedRun = casingRunData.find(run => run.CasingRunID === job.CasingRunID);
                                return (
                                    <div key={job.CementJobID} className="cement-card">
                                        <div className="cement-header">
                                            <h4>Cement Job #{job.CementJobID}</h4>
                                            <div className="cement-actions">
                                                {(role === 'admin' || role === 'read-write') && (
                                                    <>
                                                        <button
                                                            className="edit-btn"
                                                            onClick={() => handleEditRecord('CementJob', job)}
                                                        >
                                                            ✏️
                                                        </button>
                                                        <button
                                                            className="delete-btn"
                                                            onClick={() => handleDeleteRecord('CementJob', job)}
                                                        >
                                                            🗑️
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="cement-details">
                                            <span><strong>Casing:</strong> {associatedRun?.CasingSize} {associatedRun?.Grade}</span>
                                            <span><strong>Volume:</strong> {job.CementVolume}m³</span>
                                            <span><strong>Job Date:</strong> {job.JobDate && new Date(job.JobDate).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CollapsibleSection>
                </div>
            )}

            {activeTab === 'activities' && (
                <CollapsibleSection
                    title="Daily Casing Activities"
                    sectionId="activities"
                    isEmpty={dailyCasingData.length === 0}
                    onAdd={() => handleAddRecord('DailyCasing')}
                >
                    {renderTimelineView()}
                </CollapsibleSection>
            )}

            {activeTab === 'runs' && (
                <CollapsibleSection
                    title="Casing Runs"
                    sectionId="runs"
                    isEmpty={casingRunData.length === 0}
                    onAdd={() => handleAddRecord('CasingRun')}
                >
                    {renderCasingRunsTable()}
                </CollapsibleSection>
            )}

            {activeTab === 'cement' && (
                <CollapsibleSection
                    title="Cement Jobs"
                    sectionId="cement"
                    isEmpty={cementJobData.length === 0}
                    onAdd={() => handleAddRecord('CementJob')}
                >
                    {renderCementJobsTable()}
                </CollapsibleSection>
            )}

            {activeTab === 'summary' && (
                <CollapsibleSection
                    title="Casing Summary by Report Date"
                    sectionId="summary"
                    isEmpty={casingData.length === 0}
                    onAdd={() => handleAddRecord('Casing')}
                >
                    {renderCasingSummaryTable()}
                </CollapsibleSection>
            )}

            {/* Data Form Modal */}
            {showDataForm && (
                <div className="modal-overlay">
                    <div className="modal-content data-form-modal">
                        {dataFormConfig.formType === 'dual' ? (
                            <DataForm
                                tables={dataFormConfig.tables}
                                identityColumns={dataFormConfig.identityColumns}
                                matchKey={dataFormConfig.matchKey}
                                recordId={dataFormConfig.recordId}
                                defaultParentData={dataFormConfig.defaultParentData}
                                hiddenParentFields={dataFormConfig.hiddenParentFields}
                                columnsPerRow={3}
                                onSave={handleDataFormSave}
                                onCancel={handleCancelForm}
                                title={dataFormConfig.title}
                            />
                        ) : (
                            <DataForm
                                data={dataFormConfig.data}
                                tableName={dataFormConfig.tableName}
                                columnsPerRow={2}
                                onSave={handleDataFormSave}
                                onCancel={handleCancelForm}
                                title={dataFormConfig.title}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Tally Modal */}
            {renderTallyModal()}

            {/* Toast Message */}
            {toast && (
                <div className="toast-message">
                    {toast}
                </div>
            )}
        </div>
    );
};

export default WellCasingCementing;
